import {
  GetAllContentCollectionQuery,
  SortDirection,
  CollectionLayoutsResponse,
  SmartQueryOperatorSymbol,
  ContentCollectionsApi,
  ContentCollectionResponse,
  ContentCollectionBody,
  ContentCollectionType,
  SmartQueryParametersResponse,
  SmartQueryBody,
  Top10CountryInfoResponse,
  Top10CollectionResponse
} from '../../../API';
import { Paginated, useLocales } from '../../../hooks';
import { useSmartQueryAPI } from '../../../hooks/API/SmartQuery/useSmartQueryAPI';
import { DEFAULT_PAGINATION_LIMIT } from '../../../utils/appDefaults';
import {
  DataManagerHook,
  DataManagerHookReturnType,
  IDataManagerHookProps,
  DataManagerHelpers
} from '../../DataManager';
import { uniq } from 'lodash-es';
import { saveAsFile } from '../../../utils/saveAsFile';
import { serializeSmartQuery } from '../../../utils/queryBuilder';
import {
  mergeTop10CountryInfoWithApiOnlyCountries,
  top10PublishedCollectionsToCountryInfoRows
} from '../../../utils/top10PublishedTiersByCountry';

export interface CollectionsHookReturnType
  extends DataManagerHookReturnType<ContentCollectionResponse, ContentCollectionBody> {
  getPaginated: (params: GetAllContentCollectionQuery) => Promise<Paginated<ContentCollectionResponse> | undefined>;
  getRevisions: (
    entityId: string,
    limit?: number,
    page?: number
  ) => Promise<Paginated<ContentCollectionResponse> | undefined>;
  removeContentFromCollection: (collectionId: string, contentId: string) => Promise<boolean>;
  removeContentFromCollections: (contentId: string) => Promise<boolean>;
  getSmartQueryParameters: (contentTypes?: string[]) => Promise<SmartQueryParametersResponse | undefined>;
  getCollectionLayoutsById: (entity: string) => Promise<CollectionLayoutsResponse>;
  getGenres: () => Promise<string[]>;
  exportContentsCsv: (entity: string) => Promise<void>;
  allLeafNodesValuesAreDefined: (collectionQuery: SmartQueryBody | undefined) => boolean;
  getTop10CountriesInfo: () => Promise<Top10CountryInfoResponse[]>;
  validateTop10Collection: (entityId: string) => Promise<Top10CollectionResponse | null>;
}

export function CollectionsHook(
  params: IDataManagerHookProps<ContentCollectionResponse, ContentCollectionBody>
): CollectionsHookReturnType {
  const dataManagerHook = DataManagerHook<ContentCollectionResponse, ContentCollectionBody>(params);

  const api = params.useApiHook() as ContentCollectionsApi;
  const smartQueryApi = useSmartQueryAPI();

  const { t } = useLocales();
  const { handleSuccess, handleApiError } = DataManagerHelpers({ name: params.name, toString });

  const getPaginated = async ({
    limit = DEFAULT_PAGINATION_LIMIT,
    page = 1,
    sortBy = 'createdDate',
    sortDirection = SortDirection.DESC,
    ...params
  }: GetAllContentCollectionQuery) => {
    if (!dataManagerHook.fetchPaginated) return;
    return await dataManagerHook.fetchPaginated(limit, page, [
      sortBy,
      sortDirection,
      params.status,
      params.authorId,
      params.search,
      params.genreFilter,
      params.contentId,
      params.usedFilter,
      params.forSimilars,
      params.entitlements,
      params.includeCardImage,
      params.isLatestPublishedRevision,
      params.includeDeleted,
      params.hasAutoRotate,
      params.hasSmartQuery,
      params.collectionTypes,
      params.hasAssetsTheseTypes
    ]);
  };

  const getRevisions = async (
    entityId: string,
    limit?: number,
    page?: number
  ): Promise<Paginated<ContentCollectionResponse> | undefined> => {
    const response = await api.getRevisions(entityId, limit, page);
    return response.data.body as Paginated<ContentCollectionResponse>;
  };

  const removeContentFromCollection = async (collectionId: string, contentId: string) => {
    try {
      await api.removeContentFromCollection(collectionId, contentId);
      handleSuccess('delete_content', contentId, { n: 1 });
      return true;
    } catch (err) {
      console.error(err);
      handleApiError(err, 'delete_content', [contentId, err as string]);
    }
    return false;
  };

  const removeContentFromCollections = async (contentId: string) => {
    try {
      const response = await api.removeContentFromCollections(contentId);
      handleSuccess('delete_content', contentId, { n: String(response.data.body.length) });
      return true;
    } catch (err) {
      console.error(err);
      handleApiError(err, 'delete_content', contentId);
    }
    return false;
  };

  const getSmartQueryParameters = async (contentTypes?: string[]) => {
    try {
      const { data } = await smartQueryApi.getParameters(contentTypes);
      return data.body;
    } catch (err) {
      console.error(err);
      handleApiError(
        err,
        'get_smart_query_parameters',
        contentTypes && [`${t('collections.collection_types')}:`, ...contentTypes]
      );
    }
  };

  const getCollectionLayoutsById = async (entityId: string): Promise<CollectionLayoutsResponse> => {
    try {
      const { data } = await api.getCollectionLayouts(entityId);
      return data.body;
    } catch (err) {
      console.error(err);
      handleApiError(err, 'get_associated_layouts', entityId);
      return { live: [], drafts: [] };
    }
  };

  const getGenres = async (): Promise<string[]> => {
    try {
      const { data } = await api.getGenres();
      return data.body;
    } catch (err) {
      console.error(err);
      handleApiError(err, t('errors.collections.get_genres'));
      return [];
    }
  };

  const exportContentsCsv = async (entityId: string): Promise<void> => {
    try {
      const response = await api.exportContentsCsv(entityId);
      saveAsFile(response);
      handleSuccess(t('export_content_csv'));
    } catch (err) {
      console.error(err);
      handleApiError(err, t('errors.collections.export_content_csv'));
    }
  };

  const allLeafNodesValuesAreDefined = (collectionQuery: SmartQueryBody | undefined): boolean => {
    if (!collectionQuery) return true;
    return collectionQuery?.conditions?.every(
      ({ operator, value }) =>
        operator === SmartQueryOperatorSymbol.AND ||
        operator === SmartQueryOperatorSymbol.OR ||
        !(['', null, undefined] as unknown[]).includes(value)
    );
  };

  const getTop10CountriesInfo = async (): Promise<Top10CountryInfoResponse[]> => {
    try {
      const publishedTop10: ContentCollectionResponse[] = [];
      const maxPages = 500;
      for (let page = 1; page <= maxPages; page++) {
        const batch = await getPaginated({
          limit: DEFAULT_PAGINATION_LIMIT,
          page,
          collectionTypes: [ContentCollectionType.TOP_10],
          isLatestPublishedRevision: true,
          sortBy: 'createdDate',
          sortDirection: SortDirection.DESC
        });
        if (!batch?.results?.length) break;
        publishedTop10.push(...batch.results);
        if (!batch.meta.hasNextPage) break;
      }

      let rows = top10PublishedCollectionsToCountryInfoRows(publishedTop10);

      try {
        const { data } = await api.getTop10CountriesInfo();
        rows = mergeTop10CountryInfoWithApiOnlyCountries(rows, data.body ?? []);
      } catch (apiErr) {
        console.error(apiErr);
      }

      const entityIdsMissingEntitlements = uniq(
        rows
          .filter((r) => r.entityId && (!r.entitlements || r.entitlements.length === 0))
          .map((r) => r.entityId as string)
      );

      if (entityIdsMissingEntitlements.length > 0) {
        try {
          const entitlementsByEntityId = new Map<string, string[]>();
          const mergeCollections = (collections: ContentCollectionResponse[]) => {
            for (const col of collections) {
              if (!col.entitlements?.length) continue;
              const next = col.entitlements.map((e) => String(e));
              const prev = entitlementsByEntityId.get(col.entityId);
              entitlementsByEntityId.set(col.entityId, prev?.length ? uniq([...prev, ...next]) : next);
            }
          };

          const published = await api.getLatestPublishedRevisionsByIds(entityIdsMissingEntitlements);
          mergeCollections(published.data.body);

          const stillWithoutTiers = entityIdsMissingEntitlements.filter(
            (id) => !entitlementsByEntityId.get(id)?.length
          );
          if (stillWithoutTiers.length > 0) {
            const latest = await api.getLatestRevisionsByIds(stillWithoutTiers);
            mergeCollections(latest.data.body);
          }

          rows = rows.map((row) => {
            if (!row.entityId) return row;
            if (row.entitlements && row.entitlements.length > 0) return row;
            const filled = entitlementsByEntityId.get(row.entityId);
            if (!filled?.length) return row;
            return { ...row, entitlements: filled };
          });
        } catch (enrichErr) {
          console.error(enrichErr);
        }
      }

      return rows;
    } catch (err) {
      console.error(err);
      handleApiError(err, t('errors.collections.get_available_top_10_countries'));
      return [];
    }
  };

  const validateTop10Collection = async (entityId: string): Promise<Top10CollectionResponse | null> => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await (api as any).validateTop10Collection(entityId);
      return response.data.body as Top10CollectionResponse;
    } catch (err) {
      console.error(err);
      handleApiError(err, t('errors.collections.validate_top10_collection'), entityId);
      return null;
    }
  };

  const update = async (
    id: string,
    record: ContentCollectionResponse,
    suppressSuccessMessage = false,
    ...args: unknown[]
  ): Promise<ContentCollectionResponse | undefined> => {
    const preparedRecord = record.query ? { ...record, query: serializeSmartQuery(record.query) } : record;
    return dataManagerHook.update(id, preparedRecord, suppressSuccessMessage, ...args);
  };

  return {
    ...dataManagerHook,
    update,
    getPaginated,
    getRevisions,
    removeContentFromCollection,
    removeContentFromCollections,
    getSmartQueryParameters,
    getCollectionLayoutsById,
    getGenres,
    exportContentsCsv,
    allLeafNodesValuesAreDefined,
    getTop10CountriesInfo,
    validateTop10Collection
  };
}
